import { useTranslation } from "react-i18next";

import {
  type UnstableGetResult as GetResult,
  type PostgrestFilterBuilder,
  type PostgrestTransformBuilder,
} from "@supabase/postgrest-js";
import {
  useMutation,
  useQueryClient,
  type DefaultError,
  type MutationFunction,
  type QueryKey,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";

import apiClient from "@/api/apiClient";
import { GLOBAL_API_SEARCH_RESULT_LIMIT } from "@/configs/api";
import { type DataTableOptions } from "@/configs/datatable";
import { notify } from "@/hooks/useNotifications";
import { type Database } from "@/supabase.types";
import { type QueryFilter } from "@/utils/filter";
import { filterNotEmpty, isDefined } from "@/utils/format";
import { getOrder, getPagination, getSearch } from "@/utils/pagination";
import { trackPosthogEvent } from "@/utils/posthog";

type Schema = Database["public"];
type Tables = Schema["Tables"];
type Rpcs = Schema["Functions"];
type AnyTableName = keyof Tables;
type AnyRpcName = keyof Rpcs;
type TableRow<T extends AnyTableName> = Tables[T]["Row"];
type TableRelations<T extends AnyTableName> = Tables[T]["Relationships"];
type RpcArgs<T extends AnyRpcName> = Rpcs[T]["Args"];
type RpcReturns<T extends AnyRpcName> = Rpcs[T]["Returns"];

// see why: https://github.com/supabase/supabase-js/issues/551
export type QueryResult<T extends AnyTableName, Q extends string> = GetResult<
  Schema,
  TableRow<T>,
  T,
  TableRelations<T>,
  Q
>;
type StopTypes = number | string | boolean | symbol | bigint | Date;
type ExcludedTypes = (...args: unknown[]) => unknown;
type Dot<T extends string, U extends string> = "" extends U ? T : `${T}.${U}`;
// prettier-ignore
type Subtract<D extends number> =
  D extends 0 ? 0 : D extends 1 ? 0 : D extends 2 ? 1 : D extends 3 ? 2 : D extends 4 ? 3 : D extends 5 ? 4 :
  D extends 6 ? 5 : D extends 7 ? 6 : D extends 8 ? 7 : D extends 9 ? 8 :  D extends 10 ? 9 : number; // Fallback for depths greater than 10
type GetKeys<T, Depth extends number = 3> = Depth extends 0
  ? "" // Stop recursion after a certain depth
  : T extends StopTypes
    ? ""
    : T extends readonly unknown[]
      ? GetKeys<T[number], Depth>
      : {
          [K in keyof T & string]: T[K] extends StopTypes
            ? K
            : T[K] extends ExcludedTypes
              ? never
              : K | Dot<K, GetKeys<T[K], Subtract<Depth>>>;
        }[keyof T & string];

interface BaseOptions<N extends AnyTableName, K extends string> {
  table: N;
  query?: K;
  searchables?: GetKeys<QueryResult<N, K>>[];
  sortables?: GetKeys<QueryResult<N, K>>[];
  filters?: Parameters<
    PostgrestFilterBuilder<Schema, TableRow<N>, QueryResult<N, K>, N, TableRelations<N>>["filter"]
  >[];
  orFilters?: Parameters<PostgrestFilterBuilder<Schema, TableRow<N>, QueryResult<N, K>, N, TableRelations<N>>["or"]>;
}

export interface ApiResourceConfig<N extends AnyTableName, K extends string> {
  table: AnyTableName;
  query?: string;
  searchables?: GetKeys<QueryResult<N, K>>[];
  sortables?: GetKeys<QueryResult<N, K>>[];
}

export interface SearchOptions extends Record<string, string | number | undefined> {
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
}

export interface SearchOptionFilters {
  searchOptions: SearchOptions;
  customFilters?: QueryFilter[];
  customOrFilters?: string;
  limit?: number;
}

export interface CreateSearchSupabaseQueryProps<N extends AnyTableName, K extends string> {
  baseOptions: BaseOptions<N, K>;
  searchOptions: SearchOptions;
  customFilters?: QueryFilter[];
  customOrFilters?: string;
  limit?: number;
}
export const createSearchSupabaseQuery = <N extends AnyTableName, K extends string>({
  baseOptions: { table, query, searchables, sortables, filters, orFilters },
  searchOptions: { sortBy, sortDirection, search },
  customFilters,
  customOrFilters,
  limit = GLOBAL_API_SEARCH_RESULT_LIMIT,
}: CreateSearchSupabaseQueryProps<N, K>) => {
  const q = apiClient.from(table).select(query, { count: "exact" });
  if (sortBy && sortDirection && sortables && sortables.length > 0) {
    const { orderBy, orderAsc } = getOrder(sortBy, sortDirection, sortables);
    if (orderBy) q.order(orderBy, { ascending: orderAsc });
  }

  if (search && search.length >= 2 && searchables && searchables.length > 0) {
    const { iLike, or } = getSearch(search, searchables);
    if (iLike.length > 0) {
      iLike.forEach((i) => {
        q.ilike(i.key, i.value);
      });
    }
    q.or(or);
  }

  if (filters && filters.length > 0) {
    for (const filter of filters) {
      q.filter(...filter);
    }
  }

  if (customFilters && customFilters.length > 0) {
    for (const filter of customFilters) {
      q.filter(...filter);
    }
  }

  if (orFilters && orFilters.length > 0) {
    q.or(...orFilters);
  }

  if (customOrFilters) {
    q.or(customOrFilters);
  }

  return q.limit(limit);
};

export interface CreatePaginatedSupabaseQueryProps<N extends AnyTableName, K extends string> {
  baseOptions: BaseOptions<N, K>;
  dataTableOptions: DataTableOptions;
  customFilters?: QueryFilter[];
  customOrFilters?: string;
}
export const createPaginatedSupabaseQuery = <N extends AnyTableName, K extends string>({
  baseOptions: { table, query, searchables, sortables, filters, orFilters },
  dataTableOptions: { page, size, sortBy, sortDirection, search },
  customFilters,
  customOrFilters,
}: CreatePaginatedSupabaseQueryProps<N, K>) => {
  const q = apiClient.from(table).select(query, { count: "estimated" });
  if (isDefined(page) && isDefined(size)) {
    const { from, to } = getPagination(page, size);
    if (isDefined(from) && isDefined(to)) {
      q.range(from, to);
    }
  }
  if (sortBy && sortDirection && sortables && sortables.length > 0) {
    const { orderBy, orderAsc } = getOrder(sortBy, sortDirection, sortables);
    if (orderBy) q.order(orderBy, { ascending: orderAsc });
  }

  if (search && search.length >= 2 && searchables && searchables.length > 0) {
    const { iLike, or } = getSearch(search, searchables);
    if (iLike.length > 0) {
      iLike.forEach((i) => {
        q.ilike(i.key, i.value);
      });
    }
    q.or(or);
  }

  if (filters && filters.length > 0) {
    for (const filter of filters) {
      q.filter(...filter);
    }
  }

  if (customFilters && customFilters.length > 0) {
    for (const filter of customFilters) {
      q.filter(...filter);
    }
  }

  if (orFilters && orFilters.length > 0) {
    q.or(...orFilters);
  }

  if (customOrFilters) {
    q.or(customOrFilters);
  }

  return q;
};

export interface ApiRpcResourceConfig<FunctionName extends AnyRpcName> {
  searchables?: GetKeys<RpcReturns<FunctionName>[]>[];
  sortables?: GetKeys<RpcReturns<FunctionName>[]>[];
}

interface BaseOptionsRpc<FunctionName extends AnyRpcName> {
  queryFnc: PostgrestTransformBuilder<Schema, RpcArgs<FunctionName>, RpcReturns<FunctionName>>;
  searchables?: GetKeys<RpcReturns<FunctionName>[]>[];
  sortables?: GetKeys<RpcReturns<FunctionName>[]>[];
  filters?: QueryFilter[];
  orFilters?: string;
}

export interface CreatePaginatedRpcSupabaseQueryProps<FunctionName extends AnyRpcName> {
  baseOptions: BaseOptionsRpc<FunctionName>;
  dataTableOptions: DataTableOptions;
  customFilters?: QueryFilter[];
  customOrFilters?: string;
}
export const createPaginatedRpcSupabaseQuery = <FunctionName extends AnyRpcName>({
  baseOptions: { queryFnc, searchables, sortables, filters, orFilters },
  dataTableOptions: { page, size, sortBy, sortDirection, search },
  customFilters,
  customOrFilters,
}: CreatePaginatedRpcSupabaseQueryProps<FunctionName>) => {
  // FIXME: this hack is needed, since PostgrestTransformBuilder is the correct type, but doesn't allow filters like PostgrestFilterBuilder
  const q = queryFnc as PostgrestFilterBuilder<Schema, RpcArgs<FunctionName>, RpcReturns<FunctionName>>;
  if (isDefined(page) && isDefined(size)) {
    const { from, to } = getPagination(page, size);
    if (isDefined(from) && isDefined(to)) {
      q.range(from, to);
    }
  }
  if (sortBy && sortDirection && sortables && sortables.length > 0) {
    const { orderBy, orderAsc } = getOrder(sortBy, sortDirection, sortables);
    if (orderBy) q.order(orderBy, { ascending: orderAsc });
  }

  if (search && search.length >= 2 && searchables && searchables.length > 0) {
    const { iLike, or } = getSearch(search, searchables);
    if (iLike.length > 0) {
      iLike.forEach((i) => {
        q.ilike(i.key, i.value);
      });
    }
    q.or(or);
  }

  if (filters && filters.length > 0) {
    for (const filter of filters) {
      q.filter(...filter);
    }
  }

  if (customFilters && customFilters.length > 0) {
    for (const filter of customFilters) {
      q.filter(...filter);
    }
  }

  if (orFilters && orFilters.length > 0) {
    q.or(orFilters);
  }

  if (customOrFilters) {
    q.or(customOrFilters);
  }

  return q;
};

interface SpawnListApiFunction<T, K> {
  table: T;
  query: K;
}
export function spawnListApiFunction<T extends AnyTableName, K extends string>({
  table,
  query,
}: SpawnListApiFunction<T, K>) {
  return async () => {
    const q = apiClient.from(table).select(query);
    const { data, error } = await q;
    if (error) throw error;
    return data;
  };
}

interface SpawnListInApiFunction<T, K> {
  table: T;
  query: K;
}
export function spawnListInApiFunction<T extends AnyTableName, K extends string>({
  table,
  query,
}: SpawnListInApiFunction<T, K>) {
  return async ({ keyIn, idsIn }: { keyIn?: string; idsIn?: string[] } = {}) => {
    const q = apiClient.from(table).select(query);
    // @ts-expect-error Supabase typing is hard
    if (keyIn && idsIn) q.in(keyIn, idsIn);
    const { data, error } = await q;
    if (error) throw error;
    return data;
  };
}

interface SpawnGetApiFunction<T, K> {
  table: T;
  query: K;
}
export function spawnGetApiFunction<T extends AnyTableName, K extends string>({
  table,
  query,
}: SpawnGetApiFunction<T, K>) {
  return async ({ match }: { match: Partial<Tables[T]["Row"]> }) => {
    const q = apiClient.from(table).select(query).match(match).maybeSingle();
    const { data, error } = await q;
    if (error) throw error;
    return data;
  };
}

interface SpawnCreateApiFunction<T, K> {
  table: T;
  query?: K;
}
export function spawnCreateApiFunction<T extends AnyTableName, K extends string>({
  table,
  query,
}: SpawnCreateApiFunction<T, K>) {
  return async ({ values }: { values: Tables[T]["Insert"] | Tables[T]["Insert"][] }) => {
    if (query) {
      // @ts-expect-error apparently the types mismatch, but in fact it works
      const q = apiClient.from(table).insert(values).select(query).maybeSingle();
      const { data, error } = await q;
      if (error) throw error;
      return data;
    }
    // @ts-expect-error apparently the types mismatch, but in fact it works
    const q = apiClient.from(table).insert(values);
    const { error } = await q;
    if (error) throw error;
    return null;
  };
}

interface SpawnCreateMultipleApiFunction<T, K> {
  table: T;
  query?: K;
}
export function spawnCreateMultipleApiFunction<T extends AnyTableName, K extends string>({
  table,
  query,
}: SpawnCreateMultipleApiFunction<T, K>) {
  return async ({ values }: { values: Tables[T]["Insert"][] }) => {
    if (query) {
      // @ts-expect-error apparently the types mismatch, but in fact it works
      const q = apiClient.from(table).insert(values).select(query);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    }
    // @ts-expect-error apparently the types mismatch, but in fact it works
    const q = apiClient.from(table).insert(values);
    const { error } = await q;
    if (error) throw error;
    return null;
  };
}

interface SpawnUpdateApiFunction<T, K> {
  table: T;
  query: K;
}
export function spawnUpdateApiFunction<T extends AnyTableName, K extends string>({
  table,
  query,
}: SpawnUpdateApiFunction<T, K>) {
  return async ({
    values,
    match,
  }: {
    values: Tables[T]["Update"] | Tables[T]["Update"][];
    match: Partial<Tables[T]["Row"]>;
  }) => {
    // @ts-expect-error apparently the types mismatch, but in fact it works
    const { data, error } = await apiClient.from(table).update(values).match(match).select(query).maybeSingle();
    if (error) throw error;
    return data;
  };
}

interface SpawnDeleteApiFunction<T> {
  table: T;
}
export function spawnDeleteApiFunction<T extends AnyTableName>({ table }: SpawnDeleteApiFunction<T>) {
  return async ({ match }: { match: Partial<Tables[T]["Row"]> }) => {
    const q = apiClient.from(table).delete().match(match);

    const { data, error } = await q;
    if (error) throw error;
    return data;
  };
}

type ErrorCallbackPromise<T extends MutationFunction<Awaited<ReturnType<T>>>> = (
  error: DefaultError | undefined,
  variables: Parameters<T>[0],
) => Promise<unknown>;
type ErrorCallbackVoid<T extends MutationFunction<Awaited<ReturnType<T>>>> = (
  error: DefaultError | undefined,
  variables: Parameters<T>[0],
) => void;

type SuccessCallbackPromise<T extends MutationFunction<Awaited<ReturnType<T>>>> = (
  data: Awaited<ReturnType<T>> | undefined,
  variables: Parameters<T>[0],
) => Promise<unknown>;
type SuccessCallbackVoid<T extends MutationFunction<Awaited<ReturnType<T>>>> = (
  data: Awaited<ReturnType<T>> | undefined,
  variables: Parameters<T>[0],
) => void;

type SettledCallbackPromise<T extends MutationFunction<Awaited<ReturnType<T>>>> = (
  data: Awaited<ReturnType<T>> | undefined,
  error: DefaultError | undefined | null,
  variables: Parameters<T>[0],
) => Promise<unknown>;
type SettledCallbackVoid<T extends MutationFunction<Awaited<ReturnType<T>>>> = (
  data: Awaited<ReturnType<T>> | undefined,
  error: DefaultError | undefined | null,
  variables: Parameters<T>[0],
) => void;

interface SpawnCreateMutationHookProps<T extends MutationFunction<Awaited<ReturnType<T>>, Parameters<T>[0]>> {
  mutationFn: T;
  notifyEntity?: string;
  analyticsEntity?: string;
  invalidationQueryKeys?: QueryKey[];
  onError?: ErrorCallbackPromise<T> | ErrorCallbackVoid<T>;
  onSuccess?: SuccessCallbackPromise<T> | SuccessCallbackVoid<T>;
  onSettled?: SettledCallbackPromise<T> | SettledCallbackVoid<T>;
}
export function spawnCreateMutationHook<T extends MutationFunction<Awaited<ReturnType<T>>, Parameters<T>[0]>>({
  mutationFn,
  notifyEntity,
  analyticsEntity,
  invalidationQueryKeys = [],
  onError,
  onSuccess,
  onSettled,
}: SpawnCreateMutationHookProps<T>) {
  return () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn,
      onMutate: async () => {
        await Promise.all(invalidationQueryKeys.map((key) => queryClient.cancelQueries({ queryKey: key })));
      },
      onError: async (error, variables) => {
        if (onError) await onError(error, variables);
        if (notifyEntity)
          notify.error(t("notify.error-reason.check-input"), t("notify.create.error", { e: notifyEntity }));
      },
      onSuccess: async (data, variables) => {
        if (onSuccess) await onSuccess(data, variables);
        if (notifyEntity) notify.success(t("notify.create.success", { e: notifyEntity }));
      },
      onSettled: async (data, error, variables) => {
        await Promise.all(invalidationQueryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key })));

        if (onSettled) await onSettled(data, error, variables);
        if (analyticsEntity) trackPosthogEvent(`create`, analyticsEntity);
      },
    });
  };
}

interface SpawnUpdateMutationHookProps<T extends MutationFunction<Awaited<ReturnType<T>>, Parameters<T>[0]>> {
  mutationFn: T;
  notifyEntity?: string;
  analyticsEntity?: string;
  entityQueryKey?: QueryKey;
  invalidationQueryKeys?: QueryKey[];
  onError?: ErrorCallbackPromise<T> | ErrorCallbackVoid<T>;
  onSuccess?: SuccessCallbackPromise<T> | SuccessCallbackVoid<T>;
  onSettled?: SettledCallbackPromise<T> | SettledCallbackVoid<T>;
}
export function spawnUpdateMutationHook<T extends MutationFunction<Awaited<ReturnType<T>>, Parameters<T>[0]>>({
  mutationFn,
  notifyEntity,
  analyticsEntity,
  entityQueryKey,
  invalidationQueryKeys = [],
  onError,
  onSuccess,
  onSettled,
}: SpawnUpdateMutationHookProps<T>) {
  return () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn,
      onMutate: async (updateData) => {
        await Promise.all(
          [...invalidationQueryKeys, entityQueryKey]
            .filter(filterNotEmpty)
            .map((key) => queryClient.cancelQueries({ queryKey: key })),
        );

        if (entityQueryKey) {
          // handle partial update data, sometimes we don't have a full entity
          const prevEntity = queryClient.getQueryData(entityQueryKey);
          const newEntity = { ...(prevEntity as object), ...updateData };
          queryClient.setQueryData(entityQueryKey, newEntity);

          return { prevEntity, newEntity };
        }
      },
      onError: async (error, variables, context) => {
        if (entityQueryKey && context?.prevEntity) queryClient.setQueryData(entityQueryKey, context.prevEntity);

        if (onError) await onError(error, variables);
        if (notifyEntity)
          notify.error(t("notify.error-reason.check-input"), t("notify.update.error", { e: notifyEntity }));
      },
      onSuccess: async (data, variables) => {
        if (onSuccess) await onSuccess(data, variables);
        if (notifyEntity) notify.success(t("notify.update.success", { e: notifyEntity }));
      },
      onSettled: async (data, error, variables) => {
        await Promise.all(
          [...invalidationQueryKeys, entityQueryKey]
            .filter(filterNotEmpty)
            .map((key) => queryClient.invalidateQueries({ queryKey: key })),
        );

        if (onSettled) await onSettled(data, error, variables);
        if (analyticsEntity) trackPosthogEvent(`update`, analyticsEntity);
      },
    });
  };
}

interface SpawnDeleteMutationHookProps<T extends MutationFunction<Awaited<ReturnType<T>>, Parameters<T>[0]>> {
  mutationFn: T;
  notifyEntity?: string;
  analyticsEntity?: string;
  invalidationQueryKeys?: QueryKey[];
  onError?: ErrorCallbackPromise<T> | ErrorCallbackVoid<T>;
  onSuccess?: SuccessCallbackPromise<T> | SuccessCallbackVoid<T>;
  onSettled?: SettledCallbackPromise<T> | SettledCallbackVoid<T>;
}
export function spawnDeleteMutationHook<T extends MutationFunction<Awaited<ReturnType<T>>, Parameters<T>[0]>>({
  mutationFn,
  notifyEntity,
  analyticsEntity,
  invalidationQueryKeys = [],
  onError,
  onSuccess,
  onSettled,
}: SpawnDeleteMutationHookProps<T>) {
  return () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn,
      onMutate: async () => {
        await Promise.all(invalidationQueryKeys.map((key) => queryClient.cancelQueries({ queryKey: key })));
      },
      onError: async (error, variables) => {
        if (onError) await onError(error, variables);
        if (notifyEntity)
          notify.error(t("notify.error-reason.check-input"), t("notify.delete.error", { e: notifyEntity }));
      },
      onSuccess: async (data, variables) => {
        if (onSuccess) await onSuccess(data, variables);
        if (notifyEntity) notify.success(t("notify.delete.success", { e: notifyEntity }));
      },
      onSettled: async (data, error, variables) => {
        await Promise.all(invalidationQueryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key })));

        if (onSettled) await onSettled(data, error, variables);
        if (analyticsEntity) trackPosthogEvent(`delete`, analyticsEntity);
      },
    });
  };
}

type PickedUseQueryResult = Pick<
  UseQueryResult,
  | "isPending"
  | "isLoading"
  | "isPaused"
  | "isFetched"
  | "isFetching"
  | "isRefetching"
  | "isStale"
  | "isSuccess"
  | "isError"
  | "isLoadingError"
  | "isRefetchError"
>;
type PickedUseSuspenseQueryResult = Pick<
  UseSuspenseQueryResult,
  | "isPending"
  | "isLoading"
  | "isPaused"
  | "isFetched"
  | "isFetching"
  | "isRefetching"
  | "isStale"
  | "isSuccess"
  | "isError"
  | "isLoadingError"
  | "isRefetchError"
>;

export const combineUseQueriesStatusFields = (results: (PickedUseQueryResult | PickedUseSuspenseQueryResult)[]) => {
  return {
    isPending: results.some((r) => r.isPending),
    isLoading: results.some((r) => r.isLoading),
    isPaused: results.some((r) => r.isPaused),
    isFetched: results.some((r) => r.isFetched),
    isFetching: results.some((r) => r.isFetching),
    isRefetching: results.some((r) => r.isRefetching),
    isStale: results.some((r) => r.isStale),
    isSuccess: results.some((r) => r.isSuccess),
    isError: results.some((r) => r.isError),
    isLoadingError: results.some((r) => r.isLoadingError),
    isRefetchError: results.some((r) => r.isRefetchError),
  };
};
